import { ArrowUpDown, SlidersHorizontal } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import type { GroupMode } from '../grouping.js';
import type { ScheduleDensity } from '../scheduleDensity.js';
import { DensityToggle } from './DensityToggle.js';

/**
 * 329 (US2, FR-002, research R6) — how the grid is laid out: Group by, row density and Reorder cars,
 * behind one button at the toolbar's right. Each control is the page's existing one with its own
 * handler and test id, so what it does and where it is remembered do not change (FR-003).
 *
 * Esc (with focus inside) or a click anywhere outside closes it; Esc returns focus to the button.
 */
export function DisplayMenu({
  groupMode,
  onGroup,
  density,
  onDensity,
  canReorder,
  onReorder,
}: {
  groupMode: GroupMode;
  onGroup: (mode: GroupMode) => void;
  density: ScheduleDensity;
  onDensity: (density: ScheduleDensity) => void;
  canReorder: boolean;
  onReorder: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !open) return;
    e.stopPropagation();
    setOpen(false);
    button.current?.focus();
  };

  return (
    <div ref={root} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={button}
        type="button"
        data-testid="schedule-display-menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className={[
          'flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-sm font-semibold text-ink',
          open ? 'border-brand-blue bg-surface-feature' : 'border-hairline-strong bg-canvas',
        ].join(' ')}
      >
        <SlidersHorizontal aria-hidden="true" size={14} />
        Display
      </button>
      {open && (
        <fieldset
          id={panelId}
          aria-label="Display"
          className="absolute left-0 z-40 mt-2 flex w-64 sm:right-0 sm:left-auto flex-col gap-3 rounded-xl border border-hairline bg-canvas p-3 shadow-xl"
        >
          {/* 319 (FR-001, FR-002): grouping is part of what is shown, so it lives in the address. */}
          <label className="flex flex-col gap-1.5 text-xs font-medium text-steel">
            Group by
            <select
              data-testid="schedule-group-by"
              value={groupMode}
              onChange={(e) => onGroup(e.target.value as GroupMode)}
              className="rounded-lg border border-hairline-strong bg-canvas px-2 py-1.5 text-sm font-normal text-ink"
            >
              <option value="none">None</option>
              <option value="location">Location</option>
              <option value="category">Category</option>
              <option value="host">Host</option>
            </select>
          </label>
          <div className="flex flex-col gap-1.5 text-xs font-medium text-steel">
            Row height
            <DensityToggle density={density} onDensity={onDensity} />
          </div>
          {canReorder && (
            <>
              <div aria-hidden="true" className="h-px bg-hairline-soft" />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onReorder();
                }}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-medium text-ink hover:bg-surface-soft"
              >
                <ArrowUpDown aria-hidden="true" size={14} />
                Reorder cars
              </button>
            </>
          )}
        </fieldset>
      )}
    </div>
  );
}
